'use client';

import { Fragment, useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import clsx from 'clsx';
import { useScrollPosition } from '@/services/scroll-position-service';
import { ContainerFullWidth } from '../Container';
import FlyMenu from './FlyMenu';
import { MobileFlyMenu } from './MobileFlyMenu';
import { MobileNavLink } from './MobileNavLink';
import { CMSLink } from '../CMSLink';
import { Media } from '@onclicks/sharedcomponents/Media/Media';
import { GeneralInformation } from '@/payload-types';
import { Media as MediaType } from '@/payload-types';
import type { IconSvg } from '@/types/IconSvg';

type NavigationItem = NonNullable<
  GeneralInformation['navigationItems']
>[number];

/**
 * Seuls les champs que le header affiche. Le document `generalInformations`
 * complet (footer, reseaux sociaux...) traverse sinon la frontiere client et
 * alourdit le payload RSC de chaque page.
 */
export type HeaderData = Pick<
  GeneralInformation,
  'navigationItems' | 'callToAction' | 'ctaIcon' | 'logo'
>;

export type HeaderProps = {
  data: HeaderData;
  /** Icones de navigation resolues cote serveur (cf. `@/utilities/resolveIcons`). */
  icons: Record<string, IconSvg>;
};

function MobileNavIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className="h-3.5 w-3.5 overflow-visible stroke-slate-700"
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <path
        d="M0 1H14M0 7H14M0 13H14"
        className={clsx(
          'origin-center transition',
          open && 'scale-90 opacity-0',
        )}
      />
      <path
        d="M2 2L12 12M12 2L2 12"
        className={clsx(
          'origin-center transition',
          !open && 'scale-90 opacity-0',
        )}
      />
    </svg>
  );
}

function MobileNavigation({
  isScroll,
  navigationItems,
}: {
  isScroll: boolean;
  navigationItems: NavigationItem[];
}) {
  return (
    <Popover>
      <PopoverButton
        className={clsx(
          'relative z-10 flex h-8 w-8 items-center justify-center ui-not-focus-visible:outline-none',
          isScroll ? 'rounded-lg bg-slate-200' : '',
        )}
        aria-label="Toggle Navigation"
      >
        {({ open }) => <MobileNavIcon open={open} />}
      </PopoverButton>
      <Transition>
        <TransitionChild
          as={Fragment}
          enter="duration-150 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="duration-150 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <PopoverBackdrop className="fixed inset-0 bg-slate-300/50" />
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter="duration-150 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <PopoverPanel
            as="div"
            id="mobile-menu"
            className="absolute inset-x-0 top-full mt-4 flex max-h-[40rem] origin-top flex-col overflow-y-auto rounded-l-2xl bg-white p-4 text-lg tracking-tight text-slate-900 shadow-xl ring-1 ring-slate-900/5"
          >
            {({ close }) => (
              <>
                <Link className="block w-full p-2 text-left " href="/" />
                <hr className="m-2 border-slate-300/40" />
                {navigationItems?.map((navItem) =>
                  navItem && navItem.links && navItem.links?.length > 0 ? (
                    <MobileFlyMenu
                      key={navItem.id}
                      navigationItems={navItem}
                      closeMenu={close}
                    />
                  ) : (
                    <span key={navItem.id}>
                      <MobileNavLink
                        className="text-left"
                        href={navItem}
                        closeMenu={close}
                      />
                      <hr className="m-2 border-slate-300/40" />
                    </span>
                  ),
                )}
              </>
            )}
          </PopoverPanel>
        </TransitionChild>
      </Transition>
    </Popover>
  );
}

export const Header: React.FC<HeaderProps> = ({ data, icons }) => {
  const scrollPosition = useScrollPosition();
  const pathname = usePathname();
  const [isScroll, setIsScroll] = useState(false);
  const { navigationItems, callToAction, ctaIcon } = data;

  useEffect(() => {
    if (scrollPosition > 2) {
      setIsScroll(true);
    }

    if (scrollPosition == 0) {
      setIsScroll(false);
    }
  }, [scrollPosition]);

  return (
    <header
      className={clsx(
        'relative z-50 py-1',
        isScroll ? 'sticky top-0 md:bg-white' : '',
      )}
    >
      <ContainerFullWidth>
        <nav className="relative flex justify-between ">
          <div className="flex items-center md:gap-x-6 lg:gap-x-12">
            {data.logo && (
              <Link
                href="/"
                // Sur l'accueil, le logo pointe vers la page courante : le
                // prefetch retelechargerait le payload RSC de la page affichee.
                prefetch={pathname === '/' ? false : undefined}
                aria-label="Home"
                className={clsx(
                  'shrink-0',
                  isScroll ? 'xsmall:hidden md:block' : 'xsmall:block',
                )}
              >
                <Media
                  resource={data.logo}
                  // h-5 (h-10 en lg) avec un ratio ~6.8:1 : 8.5rem de large, 17rem en lg.
                  size="(min-width: 64rem) 17rem, 8.5rem"
                  className="h-5 lg:h-10 w-auto shrink-0"
                  imgClassName="h-5 lg:h-10 w-auto shrink-0"
                />
              </Link>
            )}
            <div className="hidden items-center md:flex md:gap-x-6">
              {navigationItems?.map((navItem) =>
                navItem && navItem.links && navItem.links?.length > 0 ? (
                  <FlyMenu
                    key={navItem.id}
                    menuValues={navItem}
                    callToAction={callToAction!}
                    ctaIcon={ctaIcon!}
                    icons={icons}
                  />
                ) : (
                  <CMSLink
                    className="inline-block rounded-lg px-2 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                    key={navItem.id}
                    {...navItem.link}
                  />
                ),
              )}
            </div>
          </div>
          <div className="flex items-center gap-x-2 md:gap-x-4">
            <div className="-mr-1 md:hidden">
              {navigationItems && (
                <MobileNavigation
                  isScroll={isScroll}
                  navigationItems={navigationItems}
                />
              )}
            </div>
          </div>
        </nav>
      </ContainerFullWidth>
    </header>
  );
};
